import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Dimensions,
  Modal,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { useMsal } from '../../../components/auth/MsalProvider';
import { setSelectedLocation } from '../../../store/slices/uiPreferencesSlice';
import { setSelectedLocation as setAppDataSelectedLocation } from '../../../store/slices/appDataSlice';
import DLOModal from './DLOModal';
import DLODropdown from './DLODropdown';
import LocationModal from './LocationModal';
import DeviceThemeModal from './DeviceThemeModal';
import { DLODropdownOption } from '../types/dlo.types';
import { useTheme } from '../../../theme';

interface ProfileDropdownProps {
  isVisible: boolean;
  onClose: () => void;
  position?: {
    top: number;
    right: number;
  };
  userInfo?: {
    name?: string;
    id?: string;
    avatar?: string;
  };
  onLogout?: () => void;
}

// LocationModal is now a separate component in ./LocationModal

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  isVisible,
  onClose,
  position = { top: 64, right: 24 },
  userInfo = { name: 'John Doe', id: '12345678' },
  onLogout
}) => {
  // All hooks must be at the top before any conditional returns
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [isDeviceThemeModalVisible, setIsDeviceThemeModalVisible] = useState(false);
  const openDeviceThemeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { colors, resolvedTheme } = useTheme();

  const dispatch = useDispatch();
  
  // Get locations and selected location from Redux store
  const { locations } = useSelector((state: RootState) => state.appData);
  const storedLocation = useSelector((state: RootState) => state.uiPreferences.selectedLocation);

  // Get user name from Redux auth state
  const userName = useSelector((state: RootState) =>
    state.auth.user?.claims?.name || state.auth.user?.name
  ) || "Guest";

  // Get MSAL signOut function
  const { signOut } = useMsal();

  // Map locations from Redux store to dropdown format - similar to LhSidebar
  const locationOptions: DLODropdownOption[] = useMemo(() => {
    if (locations && locations.length > 0) {
      return locations.map((location) => {
        const option = {
          id: String(location.id || location.name), // Ensure string type
          label: location.name || location.displayText,
          value: String(location.id || location.name), // Ensure string type
        };
        return option;
      });
    }
    // Fallback to default location if none available in Redux
    return [{ id: "default", label: "Select Location", value: "default" }];
  }, [locations]);

  // Get current selected location for the dropdown
  const selectedLocation = useMemo(() => {
    if (storedLocation && locationOptions.length > 0) {
      // Find matching location in the options with string comparison
      const matchedLocation = locationOptions.find(loc => 
        String(loc.id) === String(storedLocation.id) || 
        String(loc.label) === String(storedLocation.displayText)
      );
      if (matchedLocation) {
        return matchedLocation;
      }
    }
    // Don't default to first location - return null if no match found
    // This ensures the dropdown shows placeholder when no location is selected
    return null;
  }, [storedLocation, locationOptions]);

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (userName && userName !== "Guest") {
      return userName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const screenWidth = Dimensions.get('window').width;
  const dropdownWidth = Math.min(334, screenWidth - 32); // Ensure 16px margin on each side

  // Ensure dropdown doesn't go off screen
  const adjustedRight = Math.min(position.right, screenWidth - dropdownWidth - 16);

  // Derive per-render theme-aware values so the component reacts to
  // light/dark/system changes on all platforms (web + native).
  const isDark = resolvedTheme === 'dark';
  // Dropdown card background: almostBlack in dark, near-white in light.
  const cardBg = isDark ? colors.background.page : '#FBFCFE';
  // Arrow fill must match the card background so it blends seamlessly.
  const arrowFill = cardBg;
  const dividerColor = isDark ? colors.border.divider : '#DFDFDF';
  const menuItemBg = cardBg;
  const iconFill = isDark ? '#FFFFFF' : '#3A3A3A';
  const menuLabelColor = isDark ? colors.text.secondary : '#616161';
  const logoutLabelColor = isDark ? colors.text.primary : '#3A3A3A';
  const userNameColor = isDark ? colors.text.primary : '#3A3A3A';

  const handleDropdownPress = (event: any) => {
    // Prevent clicks inside dropdown from closing it
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
  };

  const handleLocationPress = () => {
    // Close the profile dropdown first
    onClose();
    // Then open the location modal
    setIsLocationModalVisible(true);
  };

  const handleLocationModalClose = () => {
    setIsLocationModalVisible(false);
  };

  const handleDeviceThemePress = () => {
    // Close the profile dropdown first, then open the Device Theme popup
    // (same open/close pattern used for the Location menu item above).
    onClose();
    if (openDeviceThemeTimeoutRef.current) {
      clearTimeout(openDeviceThemeTimeoutRef.current);
    }
    openDeviceThemeTimeoutRef.current = setTimeout(() => {
      setIsDeviceThemeModalVisible(true);
      openDeviceThemeTimeoutRef.current = null;
    }, Platform.OS === 'web' ? 0 : 50);
  };

  const handleDeviceThemeModalClose = () => {
    setIsDeviceThemeModalVisible(false);
  };

  const handleLocationSelect = (location: DLODropdownOption | null) => {
    // This will be used by the LocationModal component
    // We don't update Redux here, only when user saves
  };

  const handleLocationSave = (newSelectedLocation: DLODropdownOption | null) => {
    if (newSelectedLocation) {
      const fullLocation = locations?.find(
        (loc) => String((loc as any).id || loc.name) === String(newSelectedLocation.id)
      );
      const locationObj = fullLocation ? {
        ...fullLocation,
        id: String((fullLocation as any).id),
        displayText: fullLocation.name || (fullLocation as any).displayText,
      } : {
        id: String(newSelectedLocation.id),
        displayText: newSelectedLocation.label,
      };

      // Update both Redux slices to keep them in sync - same as LhSidebar
      dispatch(setSelectedLocation(locationObj)); // Update uiPreferences slice
      dispatch(setAppDataSelectedLocation(locationObj)); // Update appData slice

      setIsLocationModalVisible(false);
      
      // Close the profile dropdown after location selection
      onClose();
    }
  };

  const handleLogout = async () => {
    onClose();
    await signOut();
    // onLogout prop is no longer needed since we handle logout directly
  };

  useEffect(() => {
    return () => {
      if (openDeviceThemeTimeoutRef.current) {
        clearTimeout(openDeviceThemeTimeoutRef.current);
        openDeviceThemeTimeoutRef.current = null;
      }
    };
  }, []);
  
  return (
    <>
      {isVisible && (
        <Modal
          visible={isVisible}
          transparent={true}
          animationType="none"
          onRequestClose={onClose}
        >
        {/* Full screen overlay */}
        <TouchableOpacity 
          style={styles.modalOverlay} 
          onPress={onClose}
          activeOpacity={1}
        >
          {/* Dropdown container */}
          <View style={[styles.dropdownContainer, { top: position.top, right: adjustedRight }]}>
            {/* Arrow pointing up */}
            <Svg
              width="14"
              height="8"
              viewBox="0 0 14 8"
              fill="none"
              style={styles.arrow}
            >
              <Path d={`M14 7.88373L0 7.88373L7 6.76052e-06L14 7.88373Z`} fill={arrowFill}/>
            </Svg>

            {/* Main dropdown card - wrapped in TouchableOpacity to prevent event bubbling */}
            <TouchableOpacity
              style={[styles.card, { backgroundColor: cardBg }]}
              onPress={handleDropdownPress}
              activeOpacity={1}
            >
              <View style={styles.cardContent}>
                {/* Header with avatar and user info */}
                <View style={styles.header}>
                  <View style={styles.avatar}>
                    {userInfo.avatar ? (
                      <Image
                        source={{ uri: userInfo.avatar }}
                        style={styles.avatarImage}
                        resizeMode="cover"
                      />
                    ) : (
                      <View style={styles.avatarFallback}>
                        <Text style={styles.avatarFallbackText}>
                          {getUserInitials()}
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.userInfo}>
                    <Text style={[styles.userName, { color: userNameColor }]}>
                      {userName}
                    </Text>
                  </View>
                </View>

                {/* Divider */}
                <View style={[styles.divider, { backgroundColor: dividerColor }]} />

                {/* Menu items */}
                <View style={styles.menuItems}>
                  {/* Location */}
                  <TouchableOpacity
                    style={[styles.menuItem, { backgroundColor: menuItemBg }]}
                    onPress={handleLocationPress}
                  >
                    <View style={styles.menuItemContent}>
                      <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                        <Path
                          d="M8.00001 2.08081C5.42001 2.08081 3.33334 4.16748 3.33334 6.74748C3.33334 10.2475 8.00001 15.4141 8.00001 15.4141C8.00001 15.4141 12.6667 10.2475 12.6667 6.74748C12.6667 4.16748 10.58 2.08081 8.00001 2.08081ZM4.66668 6.74748C4.66668 4.90748 6.16001 3.41414 8.00001 3.41414C9.84001 3.41414 11.3333 4.90748 11.3333 6.74748C11.3333 8.66748 9.41334 11.5408 8.00001 13.3341C6.61334 11.5541 4.66668 8.64748 4.66668 6.74748Z"
                          fill={iconFill}
                        />
                        <Path
                          d="M8.00001 8.41414C8.92049 8.41414 9.66668 7.66795 9.66668 6.74748C9.66668 5.827 8.92049 5.08081 8.00001 5.08081C7.07954 5.08081 6.33334 5.827 6.33334 6.74748C6.33334 7.66795 7.07954 8.41414 8.00001 8.41414Z"
                          fill={iconFill}
                        />
                      </Svg>
                      <Text style={[styles.menuItemLabel, { color: menuLabelColor }]}>Location</Text>
                    </View>
                  </TouchableOpacity>

                  {/* Device Theme */}
                  <TouchableOpacity
                    style={[styles.menuItem, { backgroundColor: menuItemBg }]}
                    onPress={handleDeviceThemePress}
                  >
                    <View style={styles.menuItemContent}>
                      <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                        <Path
                          d="M8 2.08081C4.87 2.08081 2.33334 4.61748 2.33334 7.74748C2.33334 10.8775 4.87 13.4141 8 13.4141C11.13 13.4141 13.6667 10.8775 13.6667 7.74748C13.6667 4.61748 11.13 2.08081 8 2.08081ZM8 12.0808C5.61334 12.0808 3.66668 10.1341 3.66668 7.74748C3.66668 5.36081 5.61334 3.41414 8 3.41414V12.0808Z"
                          fill={iconFill}
                        />
                      </Svg>
                      <Text style={[styles.menuItemLabel, { color: menuLabelColor }]}>Device Theme</Text>
                    </View>
                  </TouchableOpacity>

                  {/* Department */}
                  {/* Out of Scope: Commenting out Department Link */}
                  {/* <TouchableOpacity style={styles.menuItem}>
                    <View style={styles.menuItemContent}>
                      <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                        <Path
                          d="M14.6667 8.08083V2.7475H10V4.7475H6.00001V2.7475H1.33334V8.08083H6.00001V6.08083H7.33334V12.7475H10V14.7475H14.6667V9.41416H10V11.4142H8.66668V6.08083H10V8.08083H14.6667ZM4.66668 6.7475H2.66668V4.08083H4.66668V6.7475ZM11.3333 10.7475H13.3333V13.4142H11.3333V10.7475ZM11.3333 4.08083H13.3333V6.7475H11.3333V4.08083Z"
                          fill="#3A3A3A"
                        />
                      </Svg>
                      <Text style={styles.menuItemLabel}>Department</Text>
                    </View>
                  </TouchableOpacity> */}

                  {/* Logout */}
                  <TouchableOpacity
                    style={[styles.menuItem, { backgroundColor: menuItemBg }]}
                    onPress={handleLogout}
                  >
                    <View style={styles.menuItemContent}>
                      <Svg width="16" height="16" viewBox="0 0 16 17" fill="none">
                        <Path
                          d="M7.33334 5.41416L6.40001 6.3475L8.13334 8.08083H1.33334V9.41416H8.13334L6.40001 11.1475L7.33334 12.0808L10.6667 8.7475L7.33334 5.41416ZM13.3333 13.4142H8.00001V14.7475H13.3333C14.0667 14.7475 14.6667 14.1475 14.6667 13.4142V4.08083C14.6667 3.3475 14.0667 2.7475 13.3333 2.7475H8.00001V4.08083H13.3333V13.4142Z"
                          fill={iconFill}
                        />
                      </Svg>
                      <Text style={[styles.menuItemLabel, styles.logoutLabel, { color: logoutLabelColor }]}>Logout</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
      )}
      
      {/* Location Modal */}
      <LocationModal
        visible={isLocationModalVisible}
        onClose={handleLocationModalClose}
        selectedLocation={selectedLocation}
        locationOptions={locationOptions}
        onSave={handleLocationSave}
      />

      {/* Device Theme Modal */}
      <DeviceThemeModal
        visible={isDeviceThemeModalVisible}
        onClose={handleDeviceThemeModalClose}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownContainer: {
    position: 'absolute',
    height: 'auto',
    width: 'auto',
    flexGrow: 0,
    alignSelf: 'center',
  },
  arrow: {
    position: 'absolute',
    right: 20,
    top: 1,
    zIndex: 1,
  },
  card: {
    marginTop: 8,
    width: 334,
    // Increased from 190 to accommodate the new Device Theme menu item
    // (was sized for exactly 2 rows: Location + Logout).
    height: 230,
    // backgroundColor is applied inline so it reacts to theme changes
    // on all platforms (native StyleSheet.create() is static).
    borderRadius: 8,
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 8,
  },
  cardContent: {
    padding: 24,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
  },
  avatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  avatarFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#818181',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarFallbackText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  userInfo: {
    flex: 1,
    flexDirection: 'column',
  },
  userName: {
    // color applied inline so it reacts to theme on all platforms
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
  },
  divider: {
    height: 1,
    // backgroundColor applied inline so it reacts to theme on all platforms
    alignSelf: 'stretch',
  },
  menuItems: {
    alignSelf: 'stretch',
    gap: 15,
  },
  menuItem: {
    paddingVertical: 4,
    paddingHorizontal: 4,
    // backgroundColor applied inline so it reacts to theme on all platforms
    alignSelf: 'stretch',
  },
  menuItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  menuItemLabel: {
    flex: 1,
    // color applied inline so it reacts to theme on all platforms
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'left',
  },
  logoutLabel: {
    // color applied inline so it reacts to theme on all platforms
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
});

export default ProfileDropdown;
